import type { ThemeColorName } from '@examdojo/styles';
import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import type { ElementCreatorContext } from '../core/board.types';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import type { PointInput } from '../line/line.model';
import { createPoint } from '../point/point.create';
import type { CircleStyle, ElementTheme, PointStyle } from '../theme/theme.model';
import type { CircleArgs, CircleResult } from './circle.model';

/**
 * Resolve a theme color name to a CSS color string.
 */
function resolveColor(themeService: JsxgraphThemeService, color: ThemeColorName | undefined): string | undefined {
  if (!color) {
    return undefined;
  }
  return themeService.getColor(color);
}

/**
 * Check if input is an existing JSXGraph element.
 */
function isElement(input: PointInput): input is GeometryElement {
  return typeof input === 'object' && !Array.isArray(input) && 'elType' in input;
}

/**
 * Resolve a point input to an existing or newly created GeometryElement.
 */
function resolvePoint(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  input: PointInput,
  name: string,
  style?: PointStyle,
): GeometryElement {
  const { board } = ctx;

  if (isElement(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const found = board.elementsByName[input];
    if (!found) {
      throw new Error(`Point "${input}" not found on board`);
    }
    return found as GeometryElement;
  }

  // Create new point from coords
  const result = createPoint(ctx, themeService, theme, {
    coords: input,
    name,
    style,
  });
  return result.point;
}

/**
 * Resolve center point style with proper propagation:
 * 1. theme.point
 * 2. theme.circle.center
 * 3. centerStyle override
 */
function resolveCenterStyle(theme: ElementTheme, centerStyle?: PointStyle): PointStyle {
  let result: PointStyle = { ...theme.point };
  result = deepMerge(result, theme.circle.center);
  result = deepMerge(result, centerStyle);
  return result;
}

/**
 * Build JSXGraph circle attributes from style configuration.
 */
function buildCircleAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['circle'],
  styleOverrides?: CircleStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);

  const strokeColor = resolveColor(themeService, style.strokeColor);
  const fillColor = resolveColor(themeService, style.fillColor);

  return {
    strokeColor,
    fillColor,
    fillOpacity: style.fillOpacity,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
  };
}

/**
 * Create a circle label context provider.
 */
function createCircleLabelContext(
  name: string,
  _circle: GeometryElement,
): () => {
  type: 'curve';
  name: string;
} {
  return () => ({
    type: 'curve' as const,
    name,
  });
}

/**
 * Create a circle element with optional center point and labels.
 * Labels are now configured via the style properties:
 * - style.label: Label for the circle itself
 * - centerStyle.label: Label for the center point
 *
 * Supports three creation modes:
 * 1. centerRadius: Center point + scalar radius
 * 2. centerPoint: Center point + point on circumference
 * 3. twoPoint: Two points defining diameter
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Circle creation arguments
 * @returns Circle result with elements and optional labels
 */
export function createCircle(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: CircleArgs,
): CircleResult {
  const { board } = ctx;
  const { name = '', style, centerStyle } = args;

  // Build circle attributes
  const circleAttrs = buildCircleAttributes(themeService, theme.circle, style);

  // Resolve center style for created points
  const resolvedCenterStyle = resolveCenterStyle(theme, centerStyle);

  let circle: GeometryElement;
  let center: GeometryElement;
  let pointOnCircle: GeometryElement | undefined;
  let a: GeometryElement | undefined;
  let b: GeometryElement | undefined;

  if (args.type === 'centerRadius') {
    // Mode 1: Center + radius
    center = resolvePoint(ctx, themeService, theme, args.center, 'center', resolvedCenterStyle);

    // Create circle with center and radius
    circle = board.create('circle', [center, args.radius], circleAttrs) as GeometryElement;
  } else if (args.type === 'centerPoint') {
    // Mode 2: Center + point on circumference
    center = resolvePoint(ctx, themeService, theme, args.center, 'center', resolvedCenterStyle);
    pointOnCircle = resolvePoint(ctx, themeService, theme, args.pointOnCircle, 'P', resolvedCenterStyle);

    // Create circle with center and point on circumference
    circle = board.create('circle', [center, pointOnCircle], circleAttrs) as GeometryElement;
  } else {
    // Mode 3: Two points defining diameter (twoPoint)
    a = resolvePoint(ctx, themeService, theme, args.a, 'A', resolvedCenterStyle);
    b = resolvePoint(ctx, themeService, theme, args.b, 'B', resolvedCenterStyle);

    // Create midpoint as center (hidden)
    center = board.create('midpoint', [a, b], { visible: false }) as GeometryElement;

    // For twoPoint mode, the radius is half the distance between a and b
    // Create circle with center and one of the diameter points
    circle = board.create('circle', [center, a], circleAttrs) as GeometryElement;
  }

  const result: CircleResult = { circle, center };

  if (pointOnCircle) {
    result.pointOnCircle = pointOnCircle;
  }

  if (a && b) {
    result.a = a;
    result.b = b;
  }

  // Determine color for labels
  const labelColor = style?.strokeColor ?? 'neutral';

  // Create label for center point if configured - now from centerStyle.label
  const centerLabelConfig = resolveLabelConfig(centerStyle?.label, { color: labelColor });
  if (centerLabelConfig) {
    result.centerLabel = bindLabel({
      ctx,
      themeService,
      element: center,
      config: centerLabelConfig,
      contextProvider: createPointLabelContext('center', center),
    });
  }

  // Create label for the circle itself if configured - now from style.label
  const circleLabelConfig = resolveLabelConfig(style?.label, { color: labelColor });
  if (circleLabelConfig && name) {
    // For circle labels, we'll position at a point on the circle (top)
    // Create a glider at the top of the circle for label positioning
    const typedCircle = circle as unknown as {
      center: { X: () => number; Y: () => number };
      Radius: () => number;
    };

    const labelPoint = board.create(
      'point',
      [() => typedCircle.center.X(), () => typedCircle.center.Y() + typedCircle.Radius()],
      { visible: false },
    ) as GeometryElement;

    result.label = bindLabel({
      ctx,
      themeService,
      element: labelPoint,
      config: circleLabelConfig,
      contextProvider: createCircleLabelContext(name, circle),
    });
  }

  return result;
}
